// src/pages/work/WorkDetails.ui.sidebar.jsx
// WorkDetails Sidebar — presentational only (no data fetching/toasts)
// NEW: Only three sections -> Email Activity • Linked Records • Meta & Actions
// Removed: Client & Address, Assignees, Address editor, assignee search hooks

import React from "react";
import { Trash2 } from "lucide-react";

/* --------------------------------- helpers -------------------------------- */
const cx = (...a) =>
  a
    .flatMap((x) =>
      typeof x === "string" ? x : x && typeof x === "object" ? Object.keys(x).filter((k) => x[k]) : []
    )
    .join(" ")
    .trim();

const idStr = (v) => (v == null ? "" : String(v));

const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const Section = ({ title, actions, children, className }) => (
  <section className={cx("rounded-2xl border border-slate-200 bg-white p-5 shadow-sm", className)}>
    <div className="mb-3 flex items-center justify-between">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</h3>
      {actions}
    </div>
    {children}
  </section>
);

/* ---------------------------------- Sidebar -------------------------------- */
export default function WorkDetailsSidebar(props) {
  const {
    // data
    work,
    canMutate,
    softDeleted,

    // actions
    onOpenEntity,
    onCreateInvoice,
    onDelete,

    // flags
    converting,
    attachingInvoice,
    deleting,
  } = props;

  const canEdit = canMutate && !softDeleted;

  /* ---------------------------- Linked records ---------------------------- */
  const linkedQuoteId = idStr(work?.links?.quoteId || "");
  const linkedInvoiceId = idStr(work?.links?.invoiceId || "");

  const quoteNumber =
    work?.links?.quoteSnapshot?.number ||
    work?.links?.quoteSnapshot?.code ||
    (linkedQuoteId ? `Q-${linkedQuoteId.slice(-6).toUpperCase()}` : null);

  const invoiceNumber =
    work?.links?.invoiceSnapshot?.number ||
    work?.links?.invoiceSnapshot?.code ||
    (linkedInvoiceId ? `INV-${linkedInvoiceId.slice(-6).toUpperCase()}` : null);

  /* ------------------------------ Email stats ----------------------------- */
  const email = work?.emailStats || {};
  const lastSentAt = email?.lastSentAt || null;

  const recipientsCount =
    Number.isFinite(email?.recipientsCount)
      ? Number(email.recipientsCount)
      : Array.isArray(email?.recipients)
      ? email.recipients.length
      : null;

  const deliveredCount = Number.isFinite(email?.deliveredCount) ? Number(email.deliveredCount) : null;
  const bouncedCount = Number.isFinite(email?.bouncedCount) ? Number(email.bouncedCount) : null;
  const droppedCount = Number.isFinite(email?.droppedCount) ? Number(email.droppedCount) : null;
  const complainedCount = Number.isFinite(email?.complainedCount) ? Number(email.complainedCount) : null;
  const failedCount = Number.isFinite(email?.failedCount) ? Number(email.failedCount) : null;
  const openCount = Number.isFinite(email?.openCount) ? Number(email.openCount) : null;
  const clickCount = Number.isFinite(email?.clickCount) ? Number(email.clickCount) : null;

  const lastDeliveredAt = email?.lastDeliveredAt || null;
  const lastBouncedAt = email?.lastBouncedAt || null;
  const lastDroppedAt = email?.lastDroppedAt || null;
  const lastComplainedAt = email?.lastComplainedAt || null;
  const lastFailedAt = email?.lastFailedAt || null;
  const lastOpenAt = email?.lastOpenAt || null;
  const lastClickAt = email?.lastClickAt || null;

  // Optional public view counters (if you expose them for work notices)
  const viewCount =
    (work?.public && Number(work.public.viewCount)) ||
    (work?.viewStats && Number(work.viewStats.count)) ||
    0;
  const lastViewAt = work?.public?.lastViewAt || work?.viewStats?.lastAt || null;

  return (
    <aside className="space-y-6 lg:sticky lg:top-6 self-start">
      {/* Email Activity */}
      <Section
        title="Email Activity"
        actions={
          <div className="text-xs text-slate-600">
            {lastSentAt ? (
              <>
                Last:&nbsp;<span className="font-semibold text-slate-900">{fmtDateTime(lastSentAt)}</span>
              </>
            ) : (
              <span className="text-slate-500">Not sent yet</span>
            )}
          </div>
        }
      >
        <dl className="mt-1 space-y-2 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">Status</dt>
            <dd className="font-medium">{lastSentAt ? "Sent" : "Not sent yet"}</dd>
          </div>

          {typeof recipientsCount === "number" && recipientsCount >= 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Recipients</dt>
              <dd className="font-medium">{recipientsCount}</dd>
            </div>
          )}

          {deliveredCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Delivered</dt>
              <dd className="font-medium">
                {deliveredCount}
                {lastDeliveredAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastDeliveredAt)}</span> : null}
              </dd>
            </div>
          )}

          {openCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Opened</dt>
              <dd className="font-medium">
                {openCount}
                {lastOpenAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastOpenAt)}</span> : null}
              </dd>
            </div>
          )}

          {clickCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Clicked</dt>
              <dd className="font-medium">
                {clickCount}
                {lastClickAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastClickAt)}</span> : null}
              </dd>
            </div>
          )}

          {bouncedCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Bounced</dt>
              <dd className="font-medium">
                {bouncedCount}
                {lastBouncedAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastBouncedAt)}</span> : null}
              </dd>
            </div>
          )}

          {droppedCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Dropped</dt>
              <dd className="font-medium">
                {droppedCount}
                {lastDroppedAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastDroppedAt)}</span> : null}
              </dd>
            </div>
          )}

          {complainedCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Complaints</dt>
              <dd className="font-medium">
                {complainedCount}
                {lastComplainedAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastComplainedAt)}</span> : null}
              </dd>
            </div>
          )}

          {failedCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Failed</dt>
              <dd className="font-medium">
                {failedCount}
                {lastFailedAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastFailedAt)}</span> : null}
              </dd>
            </div>
          )}

          {viewCount > 0 && (
            <div className="flex items-center justify-between">
              <dt className="text-slate-600">Public Views</dt>
              <dd className="font-medium">
                {viewCount}
                {lastViewAt ? <span className="text-slate-500 font-normal"> · {fmtDateTime(lastViewAt)}</span> : null}
              </dd>
            </div>
          )}
        </dl>
      </Section>

      {/* Linked Records */}
      <Section title="Linked Records">
        {/* Quote */}
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
          <div className="text-sm">
            <div className="font-medium text-slate-900">Quote</div>
            {linkedQuoteId ? (
              <div className="text-slate-600">#{quoteNumber}</div>
            ) : (
              <div className="text-slate-500">No quote linked</div>
            )}
          </div>
          <div>
            {linkedQuoteId ? (
              <button
                type="button"
                onClick={() => onOpenEntity?.("quote", linkedQuoteId)}
                className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                View
              </button>
            ) : (
              <span className="text-xs text-slate-400">—</span>
            )}
          </div>
        </div>

        {/* Invoice */}
        <div className="mt-2 flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2">
          <div className="text-sm">
            <div className="font-medium text-slate-900">Invoice</div>
            {linkedInvoiceId ? (
              <div className="text-slate-600">#{invoiceNumber}</div>
            ) : (
              <div className="text-slate-500">No invoice linked</div>
            )}
          </div>
          <div>
            {linkedInvoiceId ? (
              <button
                type="button"
                onClick={() => onOpenEntity?.("invoice", linkedInvoiceId)}
                className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
              >
                View
              </button>
            ) : work?.type === "job" && canEdit ? (
              <button
                type="button"
                onClick={onCreateInvoice}
                disabled={converting || attachingInvoice || softDeleted}
                className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                title={linkedQuoteId ? "Convert linked quote to an invoice" : "Create a new invoice"}
              >
                {linkedQuoteId ? "Convert Quote → Invoice" : "Create Invoice"}
              </button>
            ) : null}
          </div>
        </div>
      </Section>

      {/* Meta / Danger */}
      <Section title="Meta & Actions">
        <dl className="space-y-1 text-sm">
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">Created</dt>
            <dd className="font-medium">{fmtDateTime(work?.createdAt || work?.meta?.createdAt)}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-slate-600">Updated</dt>
            <dd className="font-medium">{fmtDateTime(work?.updatedAt || work?.meta?.updatedAt)}</dd>
          </div>
        </dl>

        {canMutate && (
          <div className="mt-4">
            <button
              type="button"
              onClick={onDelete}
              disabled={deleting || softDeleted}
              className={cx(
                "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
                softDeleted ? "cursor-not-allowed bg-slate-200 text-slate-500" : "bg-rose-600 text-white hover:bg-rose-500"
              )}
              title={softDeleted ? "Already deleted" : "Delete"}
            >
              <Trash2 size={16} /> Delete
            </button>
          </div>
        )}
      </Section>
    </aside>
  );
}
